<template>
    <app-modal modal-id="app-product-bulk-import-modal" modal-size="large" modal-alignment="top" @close-modal="closeModal">
        <template slot="header">
            <h5 class="modal-heading">{{ $t('import_products') || 'Import Products' }}</h5>
            <button class="close outline-none" @click.prevent="closeModal"><app-icon name="x" /></button>
        </template>

        <template slot="body">
            <!-- STEP 1: upload -->
            <div v-if="step === 'upload'">
                <p class="text-muted mb-2">
                    Upload a filled-in template (.xlsx). Need the format? Use
                    <strong>Export template</strong> from the Action menu.
                </p>
                <form ref="form" :data-url="''">
                    <app-input type="dropzone" v-model="formData.file" />
                </form>
            </div>

            <!-- STEP 2: confirm (duplicates / errors found) -->
            <div v-else-if="step === 'confirm'">
                <div class="d-flex flex-wrap mb-3">
                    <div class="mr-4"><span class="text-muted">New:</span> <strong class="text-success">{{ summary.new_count }}</strong></div>
                    <div class="mr-4"><span class="text-muted">Duplicates:</span> <strong class="text-warning">{{ summary.duplicate_count }}</strong></div>
                    <div class="mr-4"><span class="text-muted">Errors:</span> <strong class="text-danger">{{ summary.error_count }}</strong></div>
                    <div><span class="text-muted">Total rows:</span> <strong>{{ summary.total }}</strong></div>
                </div>

                <div v-if="summary.new_attribute_values && summary.new_attribute_values.length" class="mb-3">
                    <div class="alert alert-info mb-2">
                        <strong>{{ summary.new_attribute_values.length }}</strong> new attribute value(s) will be created on import.
                    </div>
                    <div class="table-responsive" style="max-height:160px;overflow:auto;">
                        <table class="table table-sm">
                            <thead><tr><th>Attribute</th><th>New value</th></tr></thead>
                            <tbody>
                                <tr v-for="(a, i) in summary.new_attribute_values" :key="'a'+i">
                                    <td>{{ a.attribute }}</td><td>{{ a.value }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="summary.duplicate_count > 0" class="alert alert-warning">
                    <strong>{{ summary.duplicate_count }}</strong> row(s) match products/variants that already exist.
                    Do you want to <strong>overwrite</strong> them with the values in your file, or skip them?
                </div>

                <div v-if="summary.duplicates && summary.duplicates.length" class="mb-3">
                    <div class="font-weight-bold mb-1">Duplicate rows</div>
                    <div class="table-responsive" style="max-height:160px;overflow:auto;">
                        <table class="table table-sm">
                            <thead><tr><th>Row</th><th>Product</th><th>Variant</th></tr></thead>
                            <tbody>
                                <tr v-for="(d, i) in summary.duplicates" :key="'d'+i">
                                    <td>{{ d.row }}</td><td>{{ d.product }}</td><td>{{ d.variant }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="summary.errors && summary.errors.length" class="mb-2">
                    <div class="font-weight-bold mb-1 text-danger">Rows with errors (these will be skipped)</div>
                    <div class="table-responsive" style="max-height:160px;overflow:auto;">
                        <table class="table table-sm">
                            <thead><tr><th>Row</th><th>Product</th><th>Reason</th></tr></thead>
                            <tbody>
                                <tr v-for="(e, i) in summary.errors" :key="'e'+i">
                                    <td>{{ e.row }}</td><td>{{ e.product }}</td><td class="text-danger">{{ e.reason }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- STEP 3: result -->
            <div v-else-if="step === 'result'">
                <div class="alert alert-success">Import finished.</div>
                <div class="d-flex flex-wrap mb-3">
                    <div class="mr-4"><span class="text-muted">Created:</span> <strong class="text-success">{{ result.created }}</strong></div>
                    <div class="mr-4"><span class="text-muted">Updated:</span> <strong class="text-primary">{{ result.updated }}</strong></div>
                    <div class="mr-4"><span class="text-muted">Skipped:</span> <strong class="text-warning">{{ result.skipped }}</strong></div>
                    <div><span class="text-muted">Failed:</span> <strong class="text-danger">{{ result.failed }}</strong></div>
                </div>
                <div v-if="result.failures && result.failures.length">
                    <div class="font-weight-bold mb-1 text-danger">Failures</div>
                    <div class="table-responsive" style="max-height:200px;overflow:auto;">
                        <table class="table table-sm">
                            <thead><tr><th>Row</th><th>Reason</th></tr></thead>
                            <tbody>
                                <tr v-for="(f, i) in result.failures" :key="'f'+i">
                                    <td>{{ f.row }}</td><td class="text-danger">{{ f.reason }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <template slot="footer" class="justify-content-start">
            <!-- upload -->
            <template v-if="step === 'upload'">
                <app-submit-button :loading="analyzing" btn-class="d-inline-flex text-center mr-2"
                    :label="analyzing ? 'Analyzing...' : 'Analyze file'" @click="analyze" />
                <app-cancel-button btn-class="btn-secondary" @click="closeModal" />
            </template>

            <!-- confirm -->
            <template v-else-if="step === 'confirm'">
                <button v-if="summary.duplicate_count > 0" class="btn btn-warning mr-2" :disabled="committing"
                    @click="commit(true)">{{ committing ? 'Working...' : 'Overwrite duplicates' }}</button>
                <button class="btn btn-primary mr-2" :disabled="committing"
                    @click="commit(false)">{{ committing ? 'Working...' : (summary.duplicate_count > 0 ? 'Skip duplicates' : 'Import') }}</button>
                <app-cancel-button btn-class="btn-secondary" @click="closeModal" />
            </template>

            <!-- result -->
            <template v-else>
                <button class="btn btn-primary" @click="closeAndReload">Done</button>
            </template>
        </template>
    </app-modal>
</template>

<script>
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";
import { axiosPost } from "../../../../common/Helper/AxiosHelper";
import {
    PRODUCT_BULK_IMPORT_ANALYZE,
    PRODUCT_BULK_IMPORT_COMMIT,
} from "../../../Config/ApiUrl-CP";

export default {
    name: 'app-product-bulk-import-modal',
    mixins: [FormMixin, HelperMixin],
    data() {
        return {
            step: 'upload',
            formData: { file: null },
            analyzing: false,
            committing: false,
            summary: {},
            result: {},
        };
    },
    methods: {
        analyze() {
            if (!this.formData.file || !this.formData.file[0]) {
                this.$toastr.e('Please choose a file first.');
                return;
            }
            this.analyzing = true;
            const data = new FormData();
            data.append('import_file', this.formData.file[0]);

            axiosPost(PRODUCT_BULK_IMPORT_ANALYZE, data).then(res => {
                this.summary = res.data;
                const newValues = (this.summary.new_attribute_values || []).length;
                if (this.summary.duplicate_count === 0 && this.summary.error_count === 0 && newValues === 0) {
                    // nothing to confirm — import straight away
                    this.commit(false);
                } else {
                    this.step = 'confirm';
                }
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not analyze the file.');
            }).finally(() => {
                this.analyzing = false;
            });
        },
        commit(overwrite) {
            this.committing = true;
            const data = new FormData();
            data.append('token', this.summary.token);
            data.append('overwrite', overwrite ? 1 : 0);

            axiosPost(PRODUCT_BULK_IMPORT_COMMIT, data).then(res => {
                this.result = res.data;
                this.step = 'result';
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Import failed.');
            }).finally(() => {
                this.committing = false;
            });
        },
        closeAndReload() {
            this.$toastr.s(`Created ${this.result.created || 0}, updated ${this.result.updated || 0}.`);
            this.$emit('imported');
            this.closeModal();
        },
        closeModal() {
            $('#app-product-bulk-import-modal').modal('hide');
            this.$emit('modal-close');
        },
    },
    mounted() {
        $('#app-product-bulk-import-modal').modal('show');
    }
};
</script>
